import { Controller } from '@hotwired/stimulus'
import { Calendar, months } from 'vanilla-calendar-pro'
import { getDate, getDateString } from 'vanilla-calendar-pro/utils'

// Popup date-range picker (vanilla-calendar-pro) for a text input holding "MM/DD/YYYY - MM/DD/YYYY",
// with preset ranges. Dispatches `change` once a range or preset is picked, or the field is cleared;
// `chosenLabel` names the matching preset, mirrored server-side by Reports::TimeDashboard::DatePresets.
const SEPARATOR = ' - '

const today = () => {
  const now = new Date()
  return new Date(now.getFullYear(), now.getMonth(), now.getDate())
}
const shift = (date, { days = 0, years = 0 }) =>
  new Date(date.getFullYear() + years, date.getMonth(), date.getDate() + days)
const weekOf = (date) => [shift(date, { days: -date.getDay() }), shift(date, { days: 6 - date.getDay() })]
const monthOf = (date) => [new Date(date.getFullYear(), date.getMonth(), 1), new Date(date.getFullYear(), date.getMonth() + 1, 0)]
const quarterOf = (date) => {
  const first = date.getMonth() - (date.getMonth() % 3)
  return [new Date(date.getFullYear(), first, 1), new Date(date.getFullYear(), first + 3, 0)]
}
const yearOf = (date) => [new Date(date.getFullYear(), 0, 1), new Date(date.getFullYear(), 11, 31)]

const PRESETS = {
  'Today': () => [today(), today()],
  'Yesterday': () => [shift(today(), { days: -1 }), shift(today(), { days: -1 })],
  'This Week': () => weekOf(today()),
  'Last Week': () => weekOf(shift(today(), { days: -7 })),
  'Last 7 Days': () => [shift(today(), { days: -6 }), today()],
  'This Month': () => monthOf(today()),
  'Last Month': () => monthOf(new Date(today().getFullYear(), today().getMonth() - 1, 1)),
  'This Quarter': () => quarterOf(today()),
  'Last Quarter': () => quarterOf(new Date(today().getFullYear(), today().getMonth() - 3, 1)),
  'This Year': () => yearOf(today()),
  'Last Year': () => yearOf(shift(today(), { years: -1 })),
  'This YTD': () => [new Date(today().getFullYear(), 0, 1), today()]
}

const PRESET_BUTTONS = Object.keys(PRESETS)
  .map((label) => `<button type="button" class="btn btn-sm py-1" data-range-preset="${label}">${label}</button>`)
  .join('')
const CLEAR_BUTTON = '<div class="vc-footer"><button type="button" class="btn btn-link btn-sm p-0" data-range-clear>Clear</button></div>'

export default class extends Controller {
  chosenLabel = null
  rangeStart = null

  connect() {
    const range = this.rangeFromInput()
    this.chosenLabel = range && this.presetFor(range)

    this.calendar = new Calendar(this.element, {
      inputMode: true,
      type: 'multiple',
      extensions: [months],
      displayMonthsCount: 2,
      displayDatesOutside: false,
      selectionDatesMode: 'multiple-ranged',
      positionToInput: 'auto',
      firstWeekday: 0,
      selectedWeekends: [],
      selectedDates: this.selectedDatesFor(range),
      enableJumpToSelectedDate: true,
      onInit: (self) => this.decorate(self),
      onShow: (self) => this.syncFromInput(self),
      onChangeToInput: (self, event) => this.picked(self, event)
    })
    this.calendar.init()

    this.element.addEventListener('input', this.onTyped)
  }

  disconnect() {
    this.element.removeEventListener('input', this.onTyped)
    this.calendar?.destroy()
    this.calendar = null
  }

  decorate(self) {
    const popup = self.context.mainElement

    popup.addEventListener('mousedown', (event) => event.preventDefault())
    popup.addEventListener('click', (event) => {
      event.stopPropagation()
      this.onPopupClick(event)
    })

    self.layouts.multiple = `<div class="vc-range"><div class="vc-presets">${PRESET_BUTTONS}</div>` +
      `<div class="vc-range__calendars">${self.layouts.multiple}</div></div>${CLEAR_BUTTON}`
    self.update({ dates: false, month: false, year: false })
  }

  onPopupClick = (event) => {
    const preset = event.target.closest('[data-range-preset]')
    if (preset) {
      const label = preset.dataset.rangePreset
      const range = PRESETS[label]()
      this.calendar.set({ selectedDates: this.selectedDatesFor(range) })
      this.apply(range, label)
    } else if (event.target.closest('[data-range-clear]')) {
      this.calendar.set({ selectedDates: [] })
      this.apply(null, null)
    }
  }

  onTyped = () => {
    const range = this.rangeFromInput()
    this.chosenLabel = range && this.presetFor(range)
  }

  picked(self, event) {
    const clicked = event.target.closest('[data-vc-date]')?.dataset.vcDate
    if (!clicked) return

    if (!this.rangeStart) {
      this.rangeStart = clicked
      if (self.context.selectedDates.join() !== clicked) {
        self.set({ selectedDates: [clicked] }, { dates: true, month: false, year: false })
      }
      return
    }

    const range = [this.rangeStart, clicked].sort().map(getDate)
    this.rangeStart = null
    this.apply(range, this.presetFor(range))
  }

  apply(range, label) {
    this.chosenLabel = label
    this.element.value = range ? range.map(this.formatted).join(SEPARATOR) : ''
    this.calendar.hide()
    this.element.dispatchEvent(new Event('change', { bubbles: true }))
  }

  syncFromInput(self) {
    this.rangeStart = null
    const range = this.rangeFromInput()
    const selected = this.selectedDatesFor(range)
    const current = self.context.selectedDates

    if (selected.join() !== (current.length === 2 ? `${current[0]}:${current[1]}` : '')) {
      self.set({ selectedDates: selected })
    }

    self.context.mainElement.querySelectorAll('[data-range-preset]').forEach((button) => {
      button.classList.toggle('active', button.dataset.rangePreset === this.chosenLabel)
    })
  }

  presetFor(range) {
    const key = range.map(getDateString).join()
    return Object.keys(PRESETS).find((label) => PRESETS[label]().map(getDateString).join() === key) ?? null
  }

  selectedDatesFor(range) {
    return range ? [range.map(getDateString).join(':')] : []
  }

  rangeFromInput() {
    const parts = this.element.value.split(SEPARATOR).map((part) => this.parsed(part.trim()))
    if (parts.length !== 2 || parts.includes(null) || parts[0] > parts[1]) return null

    return parts
  }

  parsed(value) {
    const match = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/)
    if (!match) return null

    const [, month, day, year] = match.map(Number)
    const date = new Date(year, month - 1, day)
    if (date.getMonth() !== month - 1 || date.getDate() !== day) return null

    return date
  }

  formatted(date) {
    const [year, month, day] = getDateString(date).split('-')
    return `${month}/${day}/${year}`
  }
}
